import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { receiptsAPI, billsAPI } from '../utils/api';
import { useSettings } from '../hooks/useSettings';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

export default function NewReceipt() {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSettings();
  const cur = settings.currency || 'BDT';

  // Pre-fill from navigation state (coming from bill detail)
  const prefill = location.state || {};

  const [form, setForm] = useState({
    bill_id: prefill.billId || '',
    client_name: prefill.clientName || '',
    client_address: prefill.clientAddress || '',
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    amount: '',
    payment_method: 'cash',
    reference_number: '',
    notes: '',
  });
  const [bills, setBills] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    billsAPI.getAll({ limit: 100 })
      .then(res => setBills(res.data.bills))
      .catch(() => {});
  }, []);

  // When bill is selected, auto-fill client and amount
  const handleBillChange = (e) => {
    const billId = e.target.value;
    if (billId) {
      const selected = bills.find(b => String(b.id) === String(billId));
      if (selected) {
        setForm(f => ({
          ...f,
          bill_id: billId,
          client_name: selected.client_name || f.client_name,
          client_address: selected.client_address || f.client_address,
          amount: parseFloat(selected.total).toString(),
        }));
        return;
      }
    }
    setForm(f => ({ ...f, bill_id: billId }));
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) return toast.error('Client name required');
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error('Valid amount required');

    setSaving(true);
    try {
      const res = await receiptsAPI.create(form);
      toast.success('Receipt created!');
      navigate(`/receipts/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 700 }}>
      <Link to="/receipts" className="back-link">← Back to Receipts</Link>
      <h1 className="page-title">New Receipt</h1>

      <form onSubmit={handleSubmit}>
        {/* Link to Bill */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Link to Bill (Optional)</span></div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Select Bill</label>
              <select className="form-control" value={form.bill_id} onChange={handleBillChange}>
                <option value="">-- No linked bill --</option>
                {bills.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.bill_number} — {b.client_name} ({settings.currency || 'BDT'} {parseFloat(b.total).toLocaleString()}) [{b.status}]
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Linking a bill will auto-update its payment status.</div>
            </div>
          </div>
        </div>

        {/* Client Info */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Client Information</span></div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Client Name *</label>
                <input className="form-control" value={form.client_name} onChange={e => setField('client_name', e.target.value)} placeholder="Client or company name" required />
              </div>
              <div className="form-group">
                <label className="form-label">Client Address</label>
                <input className="form-control" value={form.client_address} onChange={e => setField('client_address', e.target.value)} placeholder="City, Country" />
              </div>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Payment Details</span></div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Payment Date *</label>
                <input type="date" className="form-control" value={form.payment_date} onChange={e => setField('payment_date', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Amount ({cur}) *</label>
                <input type="number" min="0" step="0.01" className="form-control" value={form.amount} onChange={e => setField('amount', e.target.value)} placeholder="0.00" required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Payment Method</label>
                <select className="form-control" value={form.payment_method} onChange={e => setField('payment_method', e.target.value)}>
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="online">Online</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Reference / Transaction No.</label>
                <input className="form-control" value={form.reference_number} onChange={e => setField('reference_number', e.target.value)} placeholder="Optional reference" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Optional notes..." />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Link to="/receipts" className="btn btn-outline">Cancel</Link>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Create Receipt'}
          </button>
        </div>
      </form>
    </div>
  );
}
